import React, { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { Warning } from '@/components/callout/Warning'
import { DeleteDialog } from '@/components/library-v2/ui/DeleteDialog'

import { useIsAtLeastAdmin } from '@/hooks/billing/useBillingRole'
import { orgCompanyProfileQueryKeys } from '@/hooks/library/types'
import { useCompanies } from '@/hooks/library/useCompanies'
import { useDeleteCompanies } from '@/hooks/library/useDeleteCompanies'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import { PaginatedResponse } from '@/types/Api'
import { UUID } from '@/types/Id'
import { EntityCompany } from '@/types/library/Entity'

interface DeleteCompaniesDialogProps {
  companyIds: UUID[]
  open: boolean
  setOpen: (isOpen: boolean) => void
  onDelete?: () => void
}

export const DeleteCompaniesDialog: React.FC<DeleteCompaniesDialogProps> = ({
  companyIds,
  open,
  setOpen,
  onDelete,
}) => {
  const queryClient = useQueryClient()

  const { activeOrgId } = useActiveOrgId()
  const { mutate: deleteCompanies } = useDeleteCompanies({
    orgId: activeOrgId,
  })

  const isAtLeastAdmin = useIsAtLeastAdmin()

  const selectOneOfIsOwnCompany = useCallback(
    (data: PaginatedResponse<EntityCompany>) => {
      return data.data.some(
        (company) =>
          company.linked_organization_id === activeOrgId &&
          companyIds.includes(company.id),
      )
    },
    [activeOrgId, companyIds],
  )

  const { data: oneOfIsOwnCompany } = useCompanies({
    orgId: activeOrgId,
    select: selectOneOfIsOwnCompany,
  })

  const onSubmit = useCallback(() => {
    if (companyIds.length > 0) {
      deleteCompanies(
        { ids: companyIds },
        {
          onError: (error) => {
            console.error('Failed to delete companies:', error)
          },
          onSuccess: async () => {
            return await queryClient.invalidateQueries({
              queryKey: orgCompanyProfileQueryKeys.organization({
                orgId: activeOrgId,
              }),
            })
          },
        },
      )
      onDelete?.()
    }
  }, [companyIds, deleteCompanies, onDelete])

  return (
    <DeleteDialog
      label={
        companyIds.length === 1 ? 'Company' : `${companyIds.length} companies`
      }
      open={open}
      setOpen={setOpen}
      onDelete={onSubmit}
      disabled={oneOfIsOwnCompany && !isAtLeastAdmin}
      className="sm:max-w-[512px]"
    >
      {oneOfIsOwnCompany && (
        <div className="pb-2">
          <Warning>
            {isAtLeastAdmin
              ? 'One of the companies you are attempting to delete is linked to your organization. Deleting it will delete your company profile. Please proceed with caution.'
              : 'One of the companies you are attempting to delete is linked to your organization. If you would like to delete it, please contact an administrator.'}
          </Warning>
        </div>
      )}
    </DeleteDialog>
  )
}
